import { ChangeEvent, useRef, useState } from 'react';
import { Search, XCircle } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import GetTicketListQuery from './GetTicketListQuery';
import UI from '../../common/UI';
import useDashboardUrlState from './useDashboardUrlState';

export interface FilterModalProps {
  eventId: string | null;
  onSuccess?: () => void;
}

const FilterModal = ({ eventId, onSuccess }: FilterModalProps) => {
  const { t } = useTranslation();

  const [urlState, setUrlState] = useDashboardUrlState();

  const { data } = useQuery(
    GetTicketListQuery,
    {
      variables: {
        event_id: eventId,
      },
      skip: !eventId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const tickets = data?.event.tickets || [];

  const form = useForm({
    values: {
      tickets: urlState.tickets,
    },
  });

  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>();

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const reset = () => {
    setSearch('');

    if (searchRef.current) {
      searchRef.current.value = '';
    }
  };

  const visibleTickets = tickets.filter((ticket) => (
    form.values.tickets.includes(ticket.id) || ticket.title.toLowerCase().includes(search.toLowerCase())
  ));

  const toggleAll = () => form.set(() => ({ tickets: [] }));

  const applyFilters = () => {
    setUrlState((urlState) => ({
      ...urlState,
      tickets: form.values.tickets,
    }));

    onSuccess?.();
  };

  return (
    <UI.Form onSubmit={() => applyFilters()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('filters')}
          </UI.H4>
          <UI.InputDescription>
            {t('dashboard_filters_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup style={{ minWidth: 0 }}>
          <UI.InputLabel>
            {t('tickets')}
          </UI.InputLabel>
          <UI.InputWrapper sc={{ mb: 1 }}>
            <UI.Span>
              <UI.Icon>
                <Search />
              </UI.Icon>
            </UI.Span>
            <UI.DebouncedInput
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder={t('search')}
              ref={searchRef}
              sc={{ px: 5 }}
            />
            {search && (
              <UI.Span>
                <UI.A onClick={() => reset()} sc={{ color: 'gray.400' }}>
                  <UI.Icon>
                    <XCircle />
                  </UI.Icon>
                </UI.A>
              </UI.Span>
            )}
          </UI.InputWrapper>
          <UI.ScrollablePanel>
            <UI.Checkbox
              checked={form.values.tickets.length === 0}
              onChange={() => toggleAll()}
            >
              {t('all_tickets')}
            </UI.Checkbox>
            {visibleTickets.map((ticket) => (
              <UI.Checkbox
                {...form.checklist('tickets', ticket.id)}
                checked={form.values.tickets.includes(ticket.id)}
                key={ticket.id}
              >
                {ticket.title}
              </UI.Checkbox>
            ))}
          </UI.ScrollablePanel>
        </UI.InputGroup>

        <UI.HR />

        <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
          {form.values.tickets.length > 0 && urlState.tickets.length > 0 && (
            <UI.Button onClick={() => toggleAll()}>
              {t('clear_filters')}
            </UI.Button>
          )}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            style={{ marginLeft: 'auto' }}
            disabled={!form.changed()}
          >
            {t('confirm')}
          </UI.Button>
        </UI.FlexContainer>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseFilterModalProps {
  eventId: string | null;
  onSuccess?: () => void;
}

export const useFilterModal = ({ eventId, onSuccess }: UseFilterModalProps) => {
  const [openModal, closeModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeModal}>
      <FilterModal eventId={eventId} onSuccess={() => { onSuccess?.(); closeModal(); }} />
    </UI.Modal>
  ), [eventId]);

  return [openModal, closeModal];
};

export default FilterModal;
